import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { io } from "socket.io-client";
import type { RealtimeEvent } from "@htzap/contracts";
import { API_URL } from "./api";
import { useToast } from "../components/ui";

const realtimeQueryRoots = new Set([
  "processes", "process", "process-alerts", "process-options", "process-templates",
  "tickets",
  "ticket",
  "contacts",
  "companies",
  "overview",
  "analytics",
  "users",
  "admin",
  "queues",
  "tags",
  "quick-replies",
  "templates",
  "team-chat",
  "delivery-assurance",
  "service-automation",
  "flows",
  "flow",
  "flow-executions",
  "scheduled-messages",
  "appointments",
  "appointment",
  "survey-settings",
  "survey-analytics",
  "login-composition"
]);

const ASSIGNED_MESSAGE_SOUND_URL = "/sounds/mensagem-em-atendimento.m4a";
const ASSIGNED_MESSAGE_SOUND_OFFSET_SECONDS = 1.36;
const ASSIGNED_MESSAGE_SOUND_DURATION_SECONDS = 0.96;

interface MessageCreatedPayload {
  direction?: "INBOUND" | "OUTBOUND";
  contactName?: string;
  assignedUserId?: string | null;
  ticketStatus?: "WAITING" | "OPEN" | "PENDING" | "RESOLVED";
}

export type IncomingNotificationKind = "assigned" | "waiting";

export function incomingNotificationKind(
  event: RealtimeEvent,
  userId?: string
): IncomingNotificationKind | null {
  if (event.type !== "message.created") return null;
  const payload = event.payload as MessageCreatedPayload;
  if (payload.direction !== "INBOUND") return null;
  if (userId && payload.assignedUserId === userId) return "assigned";
  if (payload.ticketStatus === "WAITING" && !payload.assignedUserId) {
    return "waiting";
  }
  return null;
}

export function useRealtimeUpdates({
  soundEnabled,
  userId
}: {
  soundEnabled: boolean;
  userId?: string;
}) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const soundEnabledRef = useRef(soundEnabled);
  const audioContextRef = useRef<AudioContext | null>(null);
  const assignedMessageBufferRef = useRef<AudioBuffer | null>(null);
  const assignedMessageLoadRef = useRef<Promise<AudioBuffer | null> | null>(null);

  useEffect(() => {
    soundEnabledRef.current = soundEnabled;
  }, [soundEnabled]);

  useEffect(() => {
    const loadAssignedMessageSound = (context: AudioContext) => {
      if (assignedMessageBufferRef.current) {
        return Promise.resolve(assignedMessageBufferRef.current);
      }
      if (!assignedMessageLoadRef.current) {
        assignedMessageLoadRef.current = fetch(ASSIGNED_MESSAGE_SOUND_URL)
          .then((response) => {
            if (!response.ok) throw new Error("Assigned message sound unavailable");
            return response.arrayBuffer();
          })
          .then((bytes) => context.decodeAudioData(bytes))
          .then((buffer) => {
            assignedMessageBufferRef.current = buffer;
            return buffer;
          })
          .catch(() => null);
      }
      return assignedMessageLoadRef.current;
    };

    const unlockAudio = () => {
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContext();
      }
      if (audioContextRef.current.state === "suspended") {
        void audioContextRef.current.resume();
      }
      void loadAssignedMessageSound(audioContextRef.current);
    };
    window.addEventListener("pointerdown", unlockAudio, { once: true });
    window.addEventListener("keydown", unlockAudio, { once: true });

    const socket = io(`${API_URL}/realtime`, {
      withCredentials: true,
      transports: ["websocket", "polling"]
    });

    socket.on("event", (event: RealtimeEvent) => {
      const userUpdate = event.payload as { entityType?: string; entityId?: string };
      if (event.type === "workspace.updated" && userUpdate.entityType === "User" && userUpdate.entityId === userId) {
        void queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
      }
      void queryClient.invalidateQueries({
        predicate: (query) =>
          realtimeQueryRoots.has(String(query.queryKey[0] || ""))
      });
      const notificationKind = incomingNotificationKind(event, userId);
      const waitingAlert = event.payload as {
        waitingAlert?: boolean;
        notifyTeam?: boolean;
        sequence?: number;
      };
      if (
        event.type === "ticket.updated" &&
        waitingAlert.waitingAlert &&
        waitingAlert.notifyTeam
      ) {
        toast.warning(
          waitingAlert.sequence
            ? `Atendimento #${waitingAlert.sequence} está aguardando resposta.`
            : "Um atendimento está aguardando resposta."
        );
      }
      if (notificationKind && soundEnabledRef.current) {
        if (notificationKind === "assigned") {
          if (
            !playAssignedMessageSound(
              audioContextRef.current,
              assignedMessageBufferRef.current
            )
          ) {
            playAssignedMessageFallbackChime(audioContextRef.current);
            if (audioContextRef.current) {
              void loadAssignedMessageSound(audioContextRef.current);
            }
          }
        } else {
          playWaitingQueueChime(audioContextRef.current);
        }
      }
    });

    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
      socket.disconnect();
      void audioContextRef.current?.close();
      audioContextRef.current = null;
      assignedMessageBufferRef.current = null;
      assignedMessageLoadRef.current = null;
    };
  }, [queryClient, toast, userId]);
}

function playAssignedMessageSound(
  context: AudioContext | null,
  buffer: AudioBuffer | null
) {
  if (!context || context.state !== "running" || !buffer) return false;
  const duration = Math.min(
    ASSIGNED_MESSAGE_SOUND_DURATION_SECONDS,
    buffer.duration - ASSIGNED_MESSAGE_SOUND_OFFSET_SECONDS
  );
  if (duration <= 0) return false;

  const now = context.currentTime;
  const master = context.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(0.85, now + 0.012);
  master.gain.setValueAtTime(0.85, now + Math.max(0.012, duration - 0.12));
  master.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  master.connect(context.destination);

  const source = context.createBufferSource();
  source.buffer = buffer;
  source.connect(master);
  source.start(now, ASSIGNED_MESSAGE_SOUND_OFFSET_SECONDS, duration);
  source.stop(now + duration);
  return true;
}

function playAssignedMessageFallbackChime(context: AudioContext | null) {
  if (!context || context.state !== "running") return;
  const now = context.currentTime;
  const master = context.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(0.13, now + 0.012);
  master.gain.exponentialRampToValueAtTime(0.0001, now + 0.38);
  master.connect(context.destination);

  const first = context.createOscillator();
  first.type = "sine";
  first.frequency.setValueAtTime(740, now);
  first.frequency.exponentialRampToValueAtTime(880, now + 0.12);
  first.connect(master);
  first.start(now);
  first.stop(now + 0.18);

  const second = context.createOscillator();
  second.type = "sine";
  second.frequency.setValueAtTime(1046, now + 0.13);
  second.connect(master);
  second.start(now + 0.13);
  second.stop(now + 0.36);
}

function playWaitingQueueChime(context: AudioContext | null) {
  if (!context || context.state !== "running") return;
  const now = context.currentTime;
  const master = context.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(0.11, now + 0.014);
  master.gain.exponentialRampToValueAtTime(0.0001, now + 0.58);
  master.connect(context.destination);

  [520, 620, 780].forEach((frequency, index) => {
    const tone = context.createOscillator();
    const startsAt = now + index * 0.14;
    tone.type = "triangle";
    tone.frequency.setValueAtTime(frequency, startsAt);
    tone.connect(master);
    tone.start(startsAt);
    tone.stop(startsAt + 0.11);
  });
}
